import {
  createRouter,
  createBrowserHistory,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import type { User } from '@supabase/supabase-js'

import { routeTree } from './routeTree.gen'
import { queryClient } from './lib/query-client'

/**
 * Router context shape. Routes' `beforeLoad` and `loader` functions read
 * `auth` to gate redirects and `queryClient` to prefetch data.
 *
 * `isPlatformAdmin` mirrors the `isSuperAdmin` flag from `@kairos/auth/react`
 * (resolved via `get_user_admin_status` RPC). `null` means the check has
 * not resolved yet — routes that need the value should branch on
 * loading vs. denied vs. allowed.
 */
export interface RouterContext {
  queryClient: QueryClient
  auth: {
    user: User | null
    loading: boolean
    isPlatformAdmin: boolean | null
  }
}

const history = createBrowserHistory()

/**
 * Single router instance for the app. The `auth` slice of context starts as a
 * placeholder and is replaced with the live `useAuth()` value by `RouterBridge`
 * inside `providers.tsx`, which forwards it to `<RouterProvider context={...}>`
 * so every `beforeLoad` / `loader` sees the real session.
 */
export const router = createRouter({
  routeTree,
  history,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  context: {
    queryClient,
    auth: { user: null, loading: true, isPlatformAdmin: null },
  } satisfies RouterContext,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
